/**
 * The scale colour art is drawn at (ADR-111 decision 12). The purchased icons are 32 pixels a side and the design's
 * slots are smaller than that on the UI's logical grid, so art is not drawn on the grid: one art pixel is a whole
 * number of device pixels square, the largest that fits the place's box, and the slot stays the size the design
 * draws it.
 */

/**
 * The side of every icon's art in its own pixels.
 */
export const ICON_ART_SIZE = 32;

/**
 * How many device pixels square one art pixel is drawn at: k = floor(box x d / 32), the largest whole scale at
 * which 32 art pixels fit the box. 0 means the art does not fit and the glyph is drawn instead (at d = 1 in every
 * box under 32).
 *
 * @param box The place's room for art in logical pixels: 22 for a slot inside its edge, 16 for an effect box or a
 *   toast's line.
 * @param devicePerLogical Device pixels to one logical pixel, the UI scale (`uiScale`).
 * @returns The scale k, a whole number of at least 0; the art is then 32k device pixels a side.
 * @throws RangeError when the box or the scale is not a whole number above 0.
 * @example
 * artScale(22, 3);
 * // 2: 64 device pixels of art in the 66 a 22 slot is at 1920 by 1080
 */
export function artScale(box: number, devicePerLogical: number): number {
  if (!Number.isSafeInteger(box) || box <= 0) {
    throw new RangeError(`the art's box must be a whole number above 0, not ${box}`);
  }
  if (!Number.isSafeInteger(devicePerLogical) || devicePerLogical <= 0) {
    throw new RangeError(`device pixels to a logical pixel must be a whole number above 0, not ${devicePerLogical}`);
  }
  return Math.floor((box * devicePerLogical) / ICON_ART_SIZE);
}

/**
 * The side of the part of an icon's art that shows, in art pixels: the 32 less four transparent pixels each side, which
 * every badge of the rank pack leaves clear (ADR-112 decision 4.3).
 */
export const ICON_ART_VISIBLE = 24;

/**
 * How a staff badge's art is drawn in its box (ADR-112 decision 4.3, amended 2026-10-16): the visible 24 pixels fill the
 * box at `unit` device pixels an art pixel. Where a whole number of them fits (k = floor(box x d / 24), at least 1) the
 * unit is k and `whole` is true; where none does, the unit is the fraction that fits the visible 24 to the box and `whole`
 * is false, so the art is always drawn, downscaled, and never replaced by a glyph.
 *
 * @param box The badge's box in logical pixels.
 * @param devicePerLogical Device pixels to one logical pixel, the UI scale.
 * @returns The unit, above 0, and whether it is a whole number of device pixels.
 * @throws RangeError when the box or the scale is not a whole number above 0.
 * @example
 * badgeArtUnit(8, 3);
 * // { unit: 1, whole: true }: the chat line's 8 box at d 3 is 24 device pixels, one to an art pixel
 */
export function badgeArtUnit(box: number, devicePerLogical: number): { readonly unit: number; readonly whole: boolean } {
  if (!Number.isSafeInteger(box) || box <= 0) {
    throw new RangeError(`the art's box must be a whole number above 0, not ${box}`);
  }
  if (!Number.isSafeInteger(devicePerLogical) || devicePerLogical <= 0) {
    throw new RangeError(`device pixels to a logical pixel must be a whole number above 0, not ${devicePerLogical}`);
  }
  const room = box * devicePerLogical;
  const k = Math.floor(room / ICON_ART_VISIBLE);
  return k > 0 ? { unit: k, whole: true } : { unit: room / ICON_ART_VISIBLE, whole: false };
}
